import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, ArrowLeft, Tag, Globe2, Play, Image as ImageIcon, FileText, X, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const catLabels = { yenilik: 'Yenilik', ders: 'Dərs Materialı', imtahan: 'İmtahan', tedris: 'Tədris', digər: 'Digər' };

function formatDateAZ(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
}

function getYoutubeEmbedUrl(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

export default function NewsDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [otherNews, setOtherNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/news/${id}`),
      axios.get(`${API}/api/news`)
    ]).then(([itemRes, allRes]) => {
      setItem(itemRes.data);
      setOtherNews(allRes.data.filter(n => n.id !== id).slice(0, 3));
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F4F7F6]"><div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>;
  if (!item) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F4F7F6]">
      <Globe2 className="w-16 h-16 text-[#E2E8F0] mb-4" />
      <h2 className="text-2xl font-bold text-[#1A2530] mb-2">Xəbər tapılmadı</h2>
      <Link to="/news" className="text-[#0077B6] font-semibold hover:underline mt-4">Yeniliklərə qayıt</Link>
    </div>
  );

  const shareUrl = typeof window !== 'undefined' ? encodeURIComponent(window.location.href) : '';
  const shareTitle = encodeURIComponent(item.title);
  const embedUrl = getYoutubeEmbedUrl(item.video_url);

  return (
    <div className="bg-[#F4F7F6] min-h-screen">
      {/* 1. Full-width image with back link */}
      <section className="relative">
        <div className="w-full h-[350px] md:h-[450px] overflow-hidden">
          {item.image_url ? (
            <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#03045E] via-[#0077B6] to-[#00B4D8]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/40" />
        </div>
      </section>

      {/* 2. Content card overlapping image */}
      <section className="relative -mt-20 z-10 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-xl p-8 md:p-12" data-testid="news-content-card">

            {/* Back link */}
            <Link to="/news" className="inline-flex items-center gap-2 text-[#0077B6] hover:text-[#005f92] text-sm font-semibold mb-6 transition-colors" data-testid="back-to-news">
              <ArrowLeft className="w-4 h-4" /> Yeniliklərə qayıt
            </Link>
            
            {/* Date + Share */}
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#0077B6]/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#0077B6]" />
                </div>
                <span className="text-[#1A2530] font-medium">{formatDateAZ(item.published_date || item.created_at)}</span>
                {item.category && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#0077B6]/10 text-[#0077B6] text-xs font-semibold">
                    <Tag className="w-3 h-3" /> {catLabels[item.category] || item.category}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-[#5C6B7A] mr-1">Paylaş:</span>
                <button onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,'_blank')} type="button"
                  className="group flex items-center justify-center w-9 h-9 rounded-full text-white shadow-md hover:shadow-lg hover:scale-110 transition-all duration-300 cursor-pointer border-0"
                  title="Facebook" style={{background:'linear-gradient(135deg, #1877F2, #0D5FC7)'}}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </button>
                <button onClick={() => {
                  navigator.clipboard.writeText(decodeURIComponent(shareUrl)).then(() => {
                    alert('Link kopyalandı! Instagram-da Story və ya mesaj olaraq paylaşa bilərsiniz.');
                    window.open('https://www.instagram.com/', '_blank');
                  }).catch(() => window.open('https://www.instagram.com/', '_blank'));
                }} type="button"
                  className="group flex items-center justify-center w-9 h-9 rounded-full text-white shadow-md hover:shadow-lg hover:scale-110 transition-all duration-300 cursor-pointer border-0"
                  title="Instagram" style={{background:'linear-gradient(135deg, #833AB4, #E1306C 50%, #F77737)'}}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </button>
                <button onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,'_blank')} type="button"
                  className="group flex items-center justify-center w-9 h-9 rounded-full text-white shadow-md hover:shadow-lg hover:scale-110 transition-all duration-300 cursor-pointer border-0"
                  title="LinkedIn" style={{background:'linear-gradient(135deg, #0A66C2, #004182)'}}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </button>
                <button onClick={() => window.open(`https://wa.me/?text=${shareTitle}%20${shareUrl}`,'_blank')} type="button"
                  className="group flex items-center justify-center w-9 h-9 rounded-full text-white shadow-md hover:shadow-lg hover:scale-110 transition-all duration-300 cursor-pointer border-0"
                  title="WhatsApp" style={{background:'linear-gradient(135deg, #25D366, #128C7E)'}}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </button>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0077B6] leading-normal pb-2 mb-8" data-testid="news-title">
              {item.title}
            </h1>

            {/* Summary with left border */}
            {item.summary && (
              <div className="border-l-4 border-[#0077B6] pl-6 mb-8 py-2 bg-[#F4F7F6] rounded-r-xl">
                <p className="text-lg text-[#1A2530] leading-relaxed">{item.summary}</p>
              </div>
            )}

            {/* Content (Markdown) */}
            {item.content && (
              <div className="mb-8 space-y-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-[#1A2530] [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:border-b [&_h2]:border-[#E2E8F0] [&_h2]:pb-3 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-[#0077B6] [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:text-[#5C6B7A] [&_p]:leading-relaxed [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ul]:text-[#5C6B7A] [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_ol]:text-[#5C6B7A] [&_li]:text-[#5C6B7A] [&_strong]:text-[#1A2530] [&_a]:text-[#0077B6] [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-[#0077B6] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#5C6B7A] [&_table]:w-full [&_table]:border-collapse [&_table]:my-6 [&_table]:rounded-xl [&_table]:overflow-hidden [&_table]:shadow-sm [&_thead]:bg-[#0077B6] [&_thead]:text-white [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:text-sm [&_th]:font-semibold [&_td]:px-4 [&_td]:py-3 [&_td]:text-sm [&_td]:text-[#5C6B7A] [&_td]:border-b [&_td]:border-[#E2E8F0] [&_tr:nth-child(even)]:bg-[#F4F7F6]">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.content}</ReactMarkdown>
              </div>
            )}

            {/* Video */}
            {embedUrl && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-[#0077B6]/10 flex items-center justify-center">
                    <Play className="w-4 h-4 text-[#0077B6]" />
                  </div>
                  <span className="font-bold text-[#1A2530]">Video</span>
                </div>
                <div className="aspect-video rounded-2xl overflow-hidden shadow-lg">
                  <iframe src={embedUrl} className="w-full h-full" allowFullScreen title="Video" />
                </div>
              </div>
            )}

            {/* Image Gallery */}
            {item.gallery_images && item.gallery_images.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-[#0077B6]/10 flex items-center justify-center">
                    <ImageIcon className="w-4 h-4 text-[#0077B6]" />
                  </div>
                  <span className="font-bold text-[#1A2530]">Şəkil Qalereyası</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {item.gallery_images.map((img, i) => (
                    <button key={i} onClick={() => setLightbox(i)} className="rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow focus:outline-none focus:ring-2 focus:ring-[#0077B6] cursor-zoom-in">
                      <img src={img} alt={`Şəkil ${i+1}`} className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* PDF */}
            {item.pdf_url && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-[#0077B6]/10 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-[#0077B6]" />
                  </div>
                  <span className="font-bold text-[#1A2530]">PDF Sənəd</span>
                </div>
                <a href={item.pdf_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#F4F7F6] border border-[#E2E8F0] text-[#0077B6] font-semibold hover:bg-[#0077B6]/5 transition-colors">
                  <FileText className="w-5 h-5" /> PDF sənədini aç / endir
                </a>
              </div>
            )}

            {!item.content && !embedUrl && !item.summary && (
              <p className="text-[#5C6B7A] italic">Ətraflı məzmun tezliklə əlavə olunacaq.</p>
            )}
          </motion.div>
        </div>
      </section>

      {/* 3. Other News */}
      {otherNews.length > 0 && (
        <section className="py-16 bg-white" data-testid="other-news">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A2530] mb-8">Digər Yeniliklər</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherNews.map((n) => (
                <Link key={n.id} to={`/news/${n.id}`} className="group bg-[#F4F7F6] rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300" data-testid={`other-news-${n.id}`}>
                  <div className="relative h-44 overflow-hidden">
                    {n.image_url ? <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    : <div className="w-full h-full bg-gradient-to-br from-[#03045E] to-[#0077B6]" />}
                    <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-white/90 px-2.5 py-1 rounded-md text-xs font-medium text-[#1A2530]">
                      <Calendar className="w-3 h-3 text-[#0077B6]" /> {formatDateAZ(n.created_at)}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-[#1A2530] group-hover:text-[#0077B6] transition-colors line-clamp-2">{n.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && item?.gallery_images && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] bg-black/90 flex items-center justify-center"
            onClick={() => setLightbox(null)}
          >
            <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10">
              <X className="w-6 h-6 text-white" />
            </button>
            {item.gallery_images.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + item.gallery_images.length) % item.gallery_images.length); }}
                  className="absolute left-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10">
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % item.gallery_images.length); }}
                  className="absolute right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10">
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </>
            )}
            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              src={item.gallery_images[lightbox]}
              alt=""
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-6 text-white/60 text-sm">
              {lightbox + 1} / {item.gallery_images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
